/**
 * User Management — screen 07, ported from the validated mockup.
 *
 * ⚠ **Company is an explicit attribute, never inferred from the email domain.** Ubi:
 * subcontractor staff *"instead of a CSA email, they'll have a something email"*. Half of
 * the people in this table sign in with a gmail or telenet address, so the company
 * dropdown is the only thing that decides which rows they see — which is why the dialog
 * says so under the field rather than leaving it to be discovered.
 *
 * Two departures from the mockup, both because the mockup could not know:
 *
 *   1. **The role and the company are checked against each other.** An internal role is
 *      never row-scoped, so putting one inside a contractor's company does not show them
 *      that contractor's rows — it shows them everybody's. Two dropdowns side by side, one
 *      wrong click. The pairing is refused here and again on the server, and the screen
 *      says what would have happened rather than just greying the option out.
 *   2. **The temporary password is shown on screen.** The mockup says "an onboarding email
 *      goes out on save" — and SMTP is not configured on staging, so it does not. The
 *      response reports whether the mail was actually sent; when it was not, the password
 *      is displayed once, with the fact that it must now be delivered by hand stated
 *      plainly. An account nobody can reach is a worse outcome than a password on screen.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  filterUsers,
  formatDayMonthYear,
  roleCompanyProblem,
  type AdminUserRow,
  type AuthType,
  type CompanyKind,
  type Role,
  type UserFilters,
} from "@cocon-stats/shared";
import {
  AlertTriangle,
  CheckCircle,
  Copy,
  Inbox,
  KeyRound,
  Mail,
  Pencil,
  PlusCircle,
  Search,
  ShieldCheck,
  UserX,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import { Dialog, PickCard } from "@/components/Dialog";
import { useAuth } from "@/hooks/useAuth";
import { ApiError, apiFetch } from "@/lib/api-client";

interface CompanyOption {
  id: string;
  code: string;
  name: string;
  kind: CompanyKind;
  isActive: boolean;
}

interface Directory {
  users: AdminUserRow[];
  companies: CompanyOption[];
}

const ROLE_LABELS: Record<Role, string> = {
  SYSTEM_ADMIN: "Administrator",
  UNIFIBER_INTERNAL: "Unifiber Internal",
  EXTERNAL_OPERATOR: "External Operator",
};

/** What a credential the admin now has to deliver looks like on screen. */
interface Handover {
  displayName: string;
  email: string;
  password: string;
  /** The mail server took it, so this is a copy rather than the only copy. */
  emailed: boolean;
}

export function UserManagement() {
  const { user: me } = useAuth();
  const [directory, setDirectory] = useState<Directory | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const [filters, setFilters] = useState<UserFilters>({});
  const [editing, setEditing] = useState<AdminUserRow | "new" | null>(null);
  const [handover, setHandover] = useState<Handover | null>(null);

  const load = useCallback(async () => {
    try {
      setDirectory(await apiFetch<Directory>("/admin/users"));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(
    () => (directory ? filterUsers(directory.users, filters) : []),
    [directory, filters],
  );

  /** Every write goes through here, so a refusal is always shown rather than swallowed. */
  const run = useCallback(
    async <T,>(work: () => Promise<T>, onDone?: (result: T) => void): Promise<void> => {
      setBusy(true);
      try {
        const result = await work();
        await load();
        onDone?.(result);
      } catch (err) {
        // ⚠ The server's own sentence, not a generic one. These refusals explain something
        // the administrator needs to know — "this is the last active administrator" — and
        // replacing them with "could not save" throws that away.
        toast.error(err instanceof ApiError ? err.message : "The change could not be saved.");
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  const createAccount = useCallback(
    (body: Record<string, unknown>) =>
      void run(
        () =>
          apiFetch<{ user: AdminUserRow; temporaryPassword: string | null; invited: boolean }>("/admin/users", {
            method: "POST",
            body: JSON.stringify(body),
          }),
        (result) => {
          setEditing(null);
          if (result.temporaryPassword) {
            setHandover({
              displayName: result.user.displayName,
              email: result.user.email,
              password: result.temporaryPassword,
              emailed: result.invited,
            });
          } else {
            // An Entra-only account has no password at all — there is nothing to hand over.
            toast.success(`${result.user.displayName} can sign in with Microsoft.`);
          }
        },
      ),
    [run],
  );

  const saveAccount = useCallback(
    (id: string, body: Record<string, unknown>) =>
      void run(
        () => apiFetch<{ user: AdminUserRow }>(`/admin/users/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
        (result) => {
          setEditing(null);
          toast.success(`${result.user.displayName} saved.`);
        },
      ),
    [run],
  );

  if (loading) return <div className="px-4 py-6 text-sm text-slate-400 lg:px-8">Loading…</div>;

  if (failed || !directory) {
    return (
      <div className="px-4 py-6 lg:px-8">
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
          <h3 className="font-semibold text-slate-700">The user list could not be loaded</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">Reload the page, or try again shortly.</p>
        </div>
      </div>
    );
  }

  const pending = directory.users.filter((u) => u.isActive && u.pendingEnrolment).length;
  const companies = directory.companies;

  return (
    <div className="max-w-[1400px] space-y-5 px-4 py-6 lg:px-8">
      <div className="flex flex-wrap items-center gap-4">
        <div className="gradient-band flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white">
          <Users className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-bold text-slate-900">People with access</h2>
          <p className="text-sm text-slate-500">
            {directory.users.length} {directory.users.length === 1 ? "person" : "people"} across{" "}
            {new Set(directory.users.map((u) => u.companyId)).size} companies
            {pending > 0 && <> · {pending} still to finish enrolment</>}
          </p>
        </div>
        <button type="button" className="btn gradient-btn" onClick={() => setEditing("new")}>
          <PlusCircle className="h-4 w-4" /> Add User
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative w-full sm:w-64">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            <Search className="h-4 w-4" />
          </span>
          <input
            type="text"
            value={filters.search ?? ""}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder="Search name or email"
            className="field field-icon"
          />
        </div>
        <div className="w-full sm:w-48">
        <select
          className="field"
          value={filters.companyId ?? ""}
          onChange={(e) => setFilters((f) => ({ ...f, companyId: e.target.value || undefined }))}
        >
          <option value="">All companies</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.name}
              {company.isActive ? "" : " (inactive)"}
            </option>
          ))}
        </select>
        </div>
        <div className="w-full sm:w-48">
        <select
          className="field"
          value={filters.auth ?? ""}
          onChange={(e) => setFilters((f) => ({ ...f, auth: (e.target.value || undefined) as UserFilters["auth"] }))}
        >
          <option value="">All sign-in methods</option>
          <option value="entra">Microsoft</option>
          <option value="local">Email + 2FA</option>
        </select>
        </div>
        <div className="w-full sm:w-52">
        <select
          className="field"
          value={filters.status ?? ""}
          onChange={(e) => setFilters((f) => ({ ...f, status: (e.target.value || undefined) as UserFilters["status"] }))}
        >
          <option value="">Active and disabled</option>
          <option value="active">Active only</option>
          <option value="disabled">Disabled only</option>
        </select>
        </div>
      </div>

      <section className="card overflow-visible">
        <div className="gradient-band flex items-center justify-between rounded-t-xl px-5 py-3 text-white">
          <span className="text-[11px] font-bold uppercase tracking-wider">Users</span>
          <span className="text-xs text-white/85">
            {visible.length} of {directory.users.length}
          </span>
        </div>
        <div className="custom-scrollbar overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th>User</th>
                <th>Company</th>
                <th>Role</th>
                <th>Sign-in</th>
                <th>2FA</th>
                <th>Last sign-in</th>
                <th>Status</th>
                <th style={{ width: 200 }} />
              </tr>
            </thead>
            <tbody>
              {visible.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  isMe={user.id === me?.id}
                  busy={busy}
                  onEdit={() => setEditing(user)}
                  onResetPassword={() =>
                    void run(
                      () =>
                        apiFetch<{ temporaryPassword: string; invited: boolean }>(
                          `/admin/users/${user.id}/reset-password`,
                          { method: "POST" },
                        ),
                      (result) =>
                        setHandover({
                          displayName: user.displayName,
                          email: user.email,
                          password: result.temporaryPassword,
                          emailed: result.invited,
                        }),
                    )
                  }
                  onResetTotp={() =>
                    void run(
                      () => apiFetch(`/admin/users/${user.id}/reset-totp`, { method: "POST" }),
                      () =>
                        toast.success(
                          `${user.displayName} will set up a new authenticator at their next sign-in, and is signed out everywhere now.`,
                        ),
                    )
                  }
                  onToggleActive={() =>
                    void run(
                      () =>
                        apiFetch(`/admin/users/${user.id}`, {
                          method: "PATCH",
                          body: JSON.stringify({ isActive: !user.isActive }),
                        }),
                      () =>
                        toast.success(
                          user.isActive
                            ? `${user.displayName} can no longer sign in, and any session they had is over.`
                            : `${user.displayName} can sign in again.`,
                        ),
                    )
                  }
                />
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-14 text-center">
                    <Inbox className="mx-auto h-6 w-6 text-slate-300" />
                    <p className="mt-3 text-sm text-slate-500">No users match these filters.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-xs text-slate-400">
        A new account cannot see anything until its owner has set their own password and enrolled an authenticator.
        Until then it shows as <span className="font-semibold">Pending</span>.
      </p>

      {editing && (
        <UserDialog
          user={editing === "new" ? null : editing}
          companies={companies}
          busy={busy}
          onClose={() => setEditing(null)}
          onSave={(body) => (editing === "new" ? createAccount(body) : saveAccount(editing.id, body))}
        />
      )}

      {handover && <HandoverDialog handover={handover} onClose={() => setHandover(null)} />}
    </div>
  );
}

function UserRow({
  user,
  isMe,
  busy,
  onEdit,
  onResetPassword,
  onResetTotp,
  onToggleActive,
}: {
  user: AdminUserRow;
  isMe: boolean;
  busy: boolean;
  onEdit: () => void;
  onResetPassword: () => void;
  onResetTotp: () => void;
  onToggleActive: () => void;
}) {
  const initials = user.displayName
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const microsoft = user.authType !== "LOCAL";
  const password = user.authType !== "ENTRA_ID";

  return (
    <tr className={user.isActive ? "" : "opacity-60"}>
      <td>
        <div className="flex items-center gap-3">
          <div className="gradient-band flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-800">
              {user.displayName}
              {isMe && <span className="ml-2 text-xs font-normal text-slate-400">(you)</span>}
            </p>
            <p className="truncate text-xs text-slate-400">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="text-slate-700">{user.companyName}</td>
      <td className="text-slate-600">{ROLE_LABELS[user.role]}</td>
      <td>
        <span className="flex flex-wrap gap-1">
          {microsoft && <span className="pill pill-blue">Microsoft</span>}
          {password && <span className="pill pill-purple">Email + 2FA</span>}
        </span>
      </td>
      <td>
        {/* Microsoft holds the second factor for an Entra-only account, so there is nothing
            for this column to report — an em dash, never "N/A" and never a false "None". */}
        {user.authType === "ENTRA_ID" ? (
          <span className="text-slate-300">—</span>
        ) : user.totpEnabled ? (
          <span className="pill pill-emerald">
            <span className="pill-dot" style={{ background: "currentColor" }} />
            Active
          </span>
        ) : (
          <span className="pill pill-amber">
            <span className="pill-dot" style={{ background: "currentColor" }} />
            Pending
          </span>
        )}
      </td>
      <td className="whitespace-nowrap text-slate-600">
        {user.lastLoginAt ? (
          formatDayMonthYear(new Date(user.lastLoginAt))
        ) : (
          <span className="text-slate-400">Never</span>
        )}
      </td>
      <td>
        {user.isActive ? (
          <span className="pill pill-emerald">Active</span>
        ) : (
          <span className="pill pill-slate">Disabled</span>
        )}
      </td>
      <td>
        {/*
          ⚠ Buttons, not a hidden "⋯" menu. The mockup had one; on the drawing screen the
          same pattern turned out to be the thing nobody could find, and these four actions
          are rare enough that hiding them saves nothing and costs a click plus a guess.
        */}
        <div className="flex items-center justify-end gap-1">
          <IconButton title="Edit" onClick={onEdit} disabled={busy} icon={<Pencil className="h-4 w-4" />} />
          <IconButton
            title={
              user.authType === "ENTRA_ID"
                ? "This account signs in with Microsoft — it has no password"
                : "Issue a new temporary password"
            }
            onClick={onResetPassword}
            disabled={busy || user.authType === "ENTRA_ID"}
            icon={<KeyRound className="h-4 w-4" />}
          />
          <IconButton
            title={
              user.authType === "ENTRA_ID"
                ? "Microsoft holds the second factor for this account"
                : "Clear their authenticator and sign them out everywhere"
            }
            onClick={onResetTotp}
            disabled={busy || user.authType === "ENTRA_ID"}
            icon={<ShieldCheck className="h-4 w-4" />}
          />
          <IconButton
            title={
              isMe
                ? "You cannot deactivate your own account"
                : user.isActive
                  ? "Deactivate — they are signed out at once"
                  : "Let them sign in again"
            }
            onClick={onToggleActive}
            disabled={busy || isMe}
            danger={user.isActive}
            icon={<UserX className="h-4 w-4" />}
          />
        </div>
      </td>
    </tr>
  );
}

function IconButton({
  title,
  onClick,
  disabled,
  icon,
  danger = false,
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg p-1.5 text-slate-400 transition-colors disabled:opacity-30 ${
        danger ? "hover:bg-red-50 hover:text-red-600" : "hover:bg-slate-50 hover:text-slate-700"
      }`}
    >
      {icon}
    </button>
  );
}

function UserDialog({
  user,
  companies,
  busy,
  onClose,
  onSave,
}: {
  user: AdminUserRow | null;
  companies: CompanyOption[];
  busy: boolean;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const selectable = companies.filter((c) => c.isActive || c.id === user?.companyId);
  const [email, setEmail] = useState(user?.email ?? "");
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [companyId, setCompanyId] = useState(
    user?.companyId ??
      // The first company the default role can actually live in.
      selectable.find((c) => c.kind === "CONTRACTOR")?.id ??
      selectable[0]?.id ??
      "",
  );
  const [role, setRole] = useState<Role>(user?.role ?? "EXTERNAL_OPERATOR");
  const [authType, setAuthType] = useState<AuthType>(user?.authType ?? "LOCAL");

  const company = selectable.find((c) => c.id === companyId);
  // ⚠ Checked as the dropdowns move, not on submit. The pairing is refused server-side
  // too — this is the explanation, not the guard.
  const problem = company ? roleCompanyProblem(role, company.kind) : null;

  const submit = () => {
    if (problem) return;
    onSave(
      user
        ? { displayName, companyId, role, authType }
        : { email, displayName, companyId, role, authType },
    );
  };

  return (
    <Dialog title={user ? `Edit ${user.displayName}` : "Add User"} onClose={onClose} wide>
      <form
        className="space-y-5 p-6"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="space-y-1.5">
          <label className="eyebrow">Email address</label>
          <input
            type="email"
            className="field"
            value={email}
            disabled={user !== null}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            required
          />
          <p className="text-xs text-slate-400">
            {user
              ? "The address identifies the account and cannot be changed here."
              : "Does not have to be a company address — many subcontractor staff use a personal one."}
          </p>
        </div>

        <div className="space-y-1.5">
          <label className="eyebrow">Display name</label>
          <input
            type="text"
            className="field"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Jan Peeters"
            required
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label className="eyebrow">Company</label>
            <select className="field" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              {selectable.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-400">Decides which rows they see.</p>
          </div>
          <div className="space-y-1.5">
            <label className="eyebrow">Role</label>
            <select className="field" value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {(Object.keys(ROLE_LABELS) as Role[]).map((key) => (
                <option key={key} value={key}>
                  {ROLE_LABELS[key]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/*
          ⚠ The one combination that silently breaks row scoping, explained where it is
          made. Greying the option out would stop the mistake without ever saying why an
          internal role cannot live inside a contractor — and the "why" is the interesting
          part: it would see every contractor's rows, not that one's.
        */}
        {problem && (
          <div className="flex items-start gap-2.5 rounded-lg px-3.5 py-2.5" style={{ background: "#fef2f2", border: "1px solid #fecaca" }}>
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "#b91c1c" }} />
            <p className="text-xs" style={{ color: "#b91c1c" }}>
              {problem}
            </p>
          </div>
        )}

        <div className="space-y-2">
          <label className="eyebrow">Sign-in method</label>
          <div className="grid gap-3 sm:grid-cols-3">
            <PickCard
              selected={authType === "LOCAL"}
              onSelect={() => setAuthType("LOCAL")}
              title="Email + 2FA"
              description="For subcontractors. A password and an authenticator app."
            />
            <PickCard
              selected={authType === "ENTRA_ID"}
              onSelect={() => setAuthType("ENTRA_ID")}
              title="Microsoft"
              description="Unifiber staff. No password here at all."
            />
            <PickCard
              selected={authType === "BOTH"}
              onSelect={() => setAuthType("BOTH")}
              title="Either"
              description="Both paths work. Useful while Microsoft sign-in is being proven."
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancel
          </button>
          <button type="submit" className="btn gradient-btn" disabled={busy || problem !== null}>
            {user ? "Save Changes" : "Create Account"}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * The one and only sight of a temporary password.
 *
 * ⚠ It is not stored, not logged and not retrievable — reissuing is the only way to see
 * one again. So the dialog does not close on a stray click: it has one button, and it says
 * what has to happen before it is pressed.
 */
function HandoverDialog({ handover, onClose }: { handover: Handover; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  return (
    <Dialog title="Account created" onClose={onClose}>
      <div className="space-y-4 p-6">
        <p className="text-sm text-slate-700">
          <span className="font-semibold">{handover.displayName}</span> can now sign in as{" "}
          <span className="mono text-xs">{handover.email}</span>.
        </p>

        {handover.emailed ? (
          <div className="flex items-start gap-2.5 rounded-lg px-3.5 py-2.5" style={{ background: "#eff6ff", border: "1px solid #bedbfe" }}>
            <Mail className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "#1d4fd7" }} />
            <p className="text-xs" style={{ color: "#1d4fd7" }}>
              The invitation has been emailed to them. The password below is the same one — you do not need to send it.
            </p>
          </div>
        ) : (
          <div className="flex items-start gap-2.5 rounded-lg px-3.5 py-2.5" style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "#b45309" }} />
            <p className="text-xs" style={{ color: "#b45309" }}>
              {/* ⚠ Stated, never assumed. Email is not configured on this server, so nothing
                  was sent — and an account created in silence that nobody can reach is worse
                  than a password on screen. */}
              <span className="font-semibold">No email was sent</span> — this server has no mail configured. Give them
              this password yourself, by a route you trust.
            </p>
          </div>
        )}

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-center">
          <p className="eyebrow">Temporary password</p>
          <code className="mono mt-2 block text-lg font-bold tracking-wider text-slate-800">{handover.password}</code>
          <button
            type="button"
            className="btn btn-sm btn-outline mt-3"
            onClick={() => {
              void navigator.clipboard?.writeText(handover.password).then(
                () => setCopied(true),
                () => toast.error("The clipboard is not available — select the password and copy it."),
              );
            }}
          >
            {copied ? <CheckCircle className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        <p className="text-xs text-slate-500">
          It works once. Their first sign-in forces them to choose their own password and enrol an authenticator before
          they can open anything. This password is not stored anywhere and cannot be shown again — reissue it if it is
          lost.
        </p>

        <div className="flex justify-end pt-1">
          <button type="button" className="btn gradient-btn" onClick={onClose}>
            I have it
          </button>
        </div>
      </div>
    </Dialog>
  );
}
